import {
  commercial_applications_requests_path,
  expert_dropdown_requests_path,
  marketplace_requests_path,
  request_path,
  review_system_proposals_request_for_expert_proposals_path,
  review_system_proposals_request_for_startup_proposals_path,
  sponsor_dropdown_requests_path,
  sponsor_reviewer_dashboard_requests_path,
} from "@routes/routes";
import { ProposalReviewSystemSortBy } from "@tsTypes/__generated__/enums";
import { ProposalReviewStatus } from "@tsTypes/__generated__/enums/proposal_review_status";
import {
  AddReviewersResponse,
  CommercialApplicationPageRequestsResponse,
  MarketplaceRequestsResponse,
  RequestsShowResponse,
  ReviewerDashboardRequestsResponse,
  SponsorDropdownRequest,
  SponsorDropdownRequestsResponse,
} from "@tsTypes/index";
import { ReviewSystemProposalsResponse } from "@tsTypes/proposals/review_system_proposals";
import { RequestableType, RequestPartnerTypes, RequestUseCase } from "@tsTypes/requests";
import { RfpPartnershipModel } from "@tsTypes/rfps";
import { default as api } from "./request";

export const getRequest = async (slug: string): Promise<RequestsShowResponse> => {
  const response = await api.get(request_path(slug));
  return response.data;
};

export const getRequestsForReviewerDashboard = async (
  statusFilter?: string[] | null,
  requestTypeFilter?: string[] | null,
  sortBy?: string | null
): Promise<ReviewerDashboardRequestsResponse> => {
  const response = await api.get(sponsor_reviewer_dashboard_requests_path(), {
    params: {
      status_filter: statusFilter,
      request_type_filter: requestTypeFilter,
      sort_by: sortBy,
    },
  });
  return response.data;
};

export const toggleRequestEnabled = async (
  requestId: number,
  shouldEnable: boolean
): Promise<number> => {
  const response = await api.put(request_path(requestId), {
    enabled: shouldEnable,
  });
  return response.data.request_id;
};

export const getMarketplaceRequests = async (
  {
    searchFilter,
    useCaseFilter,
    partnershipModelsFilter,
    partnerTypesFilter,
  }: {
    searchFilter: string[];
    useCaseFilter: RequestUseCase[];
    partnershipModelsFilter: RfpPartnershipModel[];
    partnerTypesFilter: RequestPartnerTypes[];
  } = {
    searchFilter: [],
    useCaseFilter: [],
    partnershipModelsFilter: [],
    partnerTypesFilter: [],
  }
): Promise<MarketplaceRequestsResponse> => {
  const response = await api.get(marketplace_requests_path(), {
    params: {
      options: JSON.stringify({
        search_filter: searchFilter,
        use_case_filter: useCaseFilter,
        partnership_model_filter: partnershipModelsFilter,
        partner_type_filter: partnerTypesFilter,
      }),
    },
  });
  if (!response) return { requests: [], scores: [] };
  return response.data;
};

export const getCommercialApplicationPageRequests = async (
  commercialApplicationId: number,
  pageNum: number
): Promise<CommercialApplicationPageRequestsResponse> => {
  const response = await api.get(
    commercial_applications_requests_path({ id: commercialApplicationId }),
    {
      params: {
        page_num: pageNum,
      },
    }
  );

  return response.data;
};

export const getRequestsForSponsorDropdowns = async (
  filterByReviewTeamAccess = false
): Promise<SponsorDropdownRequestsResponse> => {
  const response = await api.get(sponsor_dropdown_requests_path(), {
    params: {
      filter_by_review_team_access: filterByReviewTeamAccess,
    },
  });
  return response.data;
};

export const getExpertRequests = async (): Promise<SponsorDropdownRequestsResponse> => {
  const response = await api.get(expert_dropdown_requests_path());
  return response.data;
};

export const addReviewers = async ({
  requestId,
  emails,
  role,
  message,
}: {
  requestId: number;
  emails: string[];
  role: string;
  message?: string;
}): Promise<AddReviewersResponse> => {
  const response = await api.post(`/requests/add_reviewers`, {
    request_id: requestId,
    emails,
    role,
    message,
  });

  return response.data;
};

export const getRequestProposalsForReviewSystem = async (
  request: SponsorDropdownRequest | null,
  status: ProposalReviewStatus,
  options?: {
    proposalSlug: string | null;
    searchFilter: string;
    countryFilter: string[];
    organizationTypeFilter: string[];
    organizationNameFilter: string[];
    requestSolutionsFilter: string[];
    trlFilter: number[];
    evaluationStatusFilter: string[];
    bookmarkedOnlyFilter: boolean;
    criterionLeadersOnlyFilter: boolean;
    sortBy: ProposalReviewSystemSortBy;
  }
): Promise<ReviewSystemProposalsResponse> => {
  let requestPath = "";
  switch (request?.requestable_type) {
    case RequestableType.RFP:
    case RequestableType.OPEN_CALL: {
      requestPath = "/proposals";
      break;
    }
    case RequestableType.REQUEST_FOR_STARTUP: {
      requestPath = review_system_proposals_request_for_startup_proposals_path();
      break;
    }
    case RequestableType.REQUEST_FOR_EXPERT: {
      requestPath = review_system_proposals_request_for_expert_proposals_path();
      break;
    }
    default: {
      throw new Error("Invalid request");
    }
  }

  const response = await api.get(requestPath, {
    params: {
      is_for_review_system: true,
      request_id: request.id,
      status,
      proposal_slug: options?.proposalSlug,
      search_filter: options?.searchFilter,
      country_filter: options?.countryFilter,
      organization_type_filter: options?.organizationTypeFilter,
      organization_name_filter: options?.organizationNameFilter,
      request_solutions_filter: options?.requestSolutionsFilter,
      trl_filter: options?.trlFilter,
      evaluation_status_filter: options?.evaluationStatusFilter,
      bookmarked_only_filter: options?.bookmarkedOnlyFilter,
      criterion_leaders_only_filter: options?.criterionLeadersOnlyFilter,
      sort_by: options?.sortBy,
    },
  });

  return response.data;
};

export const getCanSubmitProposal = async (requestId: number): Promise<boolean> => {
  const response = await api.post(`/requests/${requestId}/can_submit_proposal`);
  return response.data.can_submit;
};
